import {
    FbaTransportBoxGroup,
    FbaTransportBoxGroupItem,
    FbaTransportPlanStatus,
    FbaTransportV2024Address,
    FbaTransportV2024Currency,
    FbaTransportV2024DeliveryWindowOption,
    FbaTransportV2024Incentive,
    FbaTransportV2024Label,
    FbaTransportV2024Operation,
    FbaTransportV2024OperationProblem,
    FbaTransportV2024OperationStatus,
    FbaTransportV2024PackingGroup,
    FbaTransportV2024PackingGroupItem,
    FbaTransportV2024PackingOption,
    FbaTransportV2024Pallet,
    FbaTransportV2024PlacementOption,
    FbaTransportV2024Plan,
    FbaTransportV2024PlanItem,
    FbaTransportV2024Shipment,
    FbaTransportV2024ShipmentBox,
    FbaTransportV2024ShipmentBoxGroupEntry,
    FbaTransportV2024ShipmentDestination,
    FbaTransportV2024ShipmentDestinationType,
    FbaTransportV2024ShipmentFreightClass,
    FbaTransportV2024ShipmentFreightInformation,
    FbaTransportV2024ShipmentGroup,
    FbaTransportV2024ShipmentItem,
    FbaTransportV2024ShipmentSource,
    FbaTransportV2024ShipmentSourceType,
    FbaTransportV2024ShipmentStatus,
    FbaTransportV2024ShippingConfiguration,
    FbaTransportV2024TransportationOption,
    LabelOwner,
    PrepOwner,
} from '@conveyr/shared-types';
import { Currency } from 'dinero.js';
import type { components, operations } from '@/api/generated/openapi';

export type FbaTransportV2024PlanRaw = components['schemas']['FbaTransportV2024Plan'];
export type PlanItemRaw = FbaTransportV2024PlanRaw['items'][number];
export type PackingOptionRaw = FbaTransportV2024PlanRaw['packing_options'][number];
export type ShippingConfigurationRaw = PackingOptionRaw['supported_shipping_configurations'][number];
export type PackingGroupRaw = PackingOptionRaw['packing_groups'][number];
export type PackingGroupItemRaw = PackingGroupRaw['items'][number];
export type PlacementOptionRaw = FbaTransportV2024PlanRaw['placement_options'][number];
export type BoxGroupRaw = components['schemas']['BoxGroup'];
export type BoxGroupItemRaw = components['schemas']['BoxGroupItem'];
export type ShipmentBoxGroupEntryRaw = components['schemas']['ShipmentBoxGroupEntry'];
export type ShipmentRaw = components['schemas']['Shipment'];
export type ShipmentDestinationRaw = ShipmentRaw['destination'];
export type ShipmentSourceRaw = ShipmentRaw['source'];
export type ContactInformationRaw = ShipmentRaw['contact_information'];
export type FreightInformationRaw = ShipmentRaw['freight_information'];
export type TrackingDetailsRaw = ShipmentRaw['tracking_details'];
export type ShipmentBoxRaw = ShipmentRaw['boxes'][number];
export type ShipmentItemRaw = ShipmentRaw['items'][number];
export type PalletRaw = ShipmentRaw['pallets'][number];
export type DeliveryWindowOptionRaw = ShipmentRaw['delivery_window_options'][number];
export type TransportationOptionRaw = ShipmentRaw['transportation_options'][number];
export type AddressRaw = components['schemas']['Address'];
export type IncentiveRaw = components['schemas']['Incentive'];
export type CurrencyRaw = components['schemas']['Currency'];
export type OperationRaw = components['schemas']['Operation'];
export type OperationProblemRaw = NonNullable<OperationRaw['problems']>[number];
export type LabelRaw = operations['get-fba-transport-v2024-plan-labels']['responses']['200']['content']['application/json']['data'][number];
export type ImportedFbaPlanRaw = operations['list-amazon-fba-transport-v2024-plans']['responses']['200']['content']['application/json']['plans'][number];

export interface ImportedFbaPlan {
    // Not on the wire: ImportAmazon.vue sets it to the local plan's id once the plan is imported.
    id?: string
    createdAt: string
    lastUpdatedAt: string
    inboundPlanId: string
    marketplaceIds: string[]
    name: string
    sourceAddress: FbaTransportV2024Address
    status: FbaTransportPlanStatus
}

const planStatuses: Record<FbaTransportV2024PlanRaw['status'], FbaTransportPlanStatus> = {
    ACTIVE: FbaTransportPlanStatus.Active,
    VOIDED: FbaTransportPlanStatus.Voided,
    SHIPPED: FbaTransportPlanStatus.Shipped,
    ERRORED: FbaTransportPlanStatus.Errored,
};

const labelOwners: Record<PlanItemRaw['label_owner'], LabelOwner> = {
    AMAZON: LabelOwner.Amazon,
    SELLER: LabelOwner.Seller,
    NONE: LabelOwner.None,
};

const prepOwners: Record<PlanItemRaw['prep_owner'], PrepOwner> = {
    AMAZON: PrepOwner.Amazon,
    SELLER: PrepOwner.Seller,
    NONE: PrepOwner.None,
};

const shipmentStatuses: Record<NonNullable<ShipmentRaw['status']>, FbaTransportV2024ShipmentStatus> = {
    ABANDONED: FbaTransportV2024ShipmentStatus.ABANDONED,
    CANCELLED: FbaTransportV2024ShipmentStatus.CANCELLED,
    CHECKED_IN: FbaTransportV2024ShipmentStatus.CHECKED_IN,
    CLOSED: FbaTransportV2024ShipmentStatus.CLOSED,
    DELETED: FbaTransportV2024ShipmentStatus.DELETED,
    DELIVERED: FbaTransportV2024ShipmentStatus.DELIVERED,
    IN_TRANSIT: FbaTransportV2024ShipmentStatus.IN_TRANSIT,
    MIXED: FbaTransportV2024ShipmentStatus.MIXED,
    READY_TO_SHIP: FbaTransportV2024ShipmentStatus.READY_TO_SHIP,
    RECEIVING: FbaTransportV2024ShipmentStatus.RECEIVING,
    SHIPPED: FbaTransportV2024ShipmentStatus.SHIPPED,
    WORKING: FbaTransportV2024ShipmentStatus.WORKING,
    UNCONFIRMED: FbaTransportV2024ShipmentStatus.UNCONFIRMED,
};

const destinationTypes: Record<ShipmentDestinationRaw['destination_type'], FbaTransportV2024ShipmentDestinationType> = {
    AMAZON_OPTIMIZED: FbaTransportV2024ShipmentDestinationType.AMAZON_OPTIMIZED,
    AMAZON_WAREHOUSE: FbaTransportV2024ShipmentDestinationType.AMAZON_WAREHOUSE,
};

const sourceTypes: Record<ShipmentSourceRaw['source_type'], FbaTransportV2024ShipmentSourceType> = {
    SELLER_FACILITY: FbaTransportV2024ShipmentSourceType.SELLER_FACILITY,
};

const transportModePreferences: Record<NonNullable<ShipmentRaw['transport_mode_preference']>, FbaTransportV2024ShipmentGroup> = {
    PARTNERED_GROUND_SMALL_PARCEL: FbaTransportV2024ShipmentGroup.PartneredSmallParcel,
    PARTNERED_FREIGHT_LTL: FbaTransportV2024ShipmentGroup.PartneredLTL,
    NON_PARTNERED_GROUND_SMALL_PARCEL: FbaTransportV2024ShipmentGroup.NonPartneredSmallParcel,
    NON_PARTNERED_FREIGHT_LTL: FbaTransportV2024ShipmentGroup.NonPartneredLTL,
};

const freightClasses: Record<NonNullable<FreightInformationRaw['freight_class']>, FbaTransportV2024ShipmentFreightClass> = {
    NONE: FbaTransportV2024ShipmentFreightClass.NONE,
    FC_50: FbaTransportV2024ShipmentFreightClass.FC_50,
    FC_55: FbaTransportV2024ShipmentFreightClass.FC_55,
    FC_60: FbaTransportV2024ShipmentFreightClass.FC_60,
    FC_65: FbaTransportV2024ShipmentFreightClass.FC_65,
    FC_70: FbaTransportV2024ShipmentFreightClass.FC_70,
    FC_77_5: FbaTransportV2024ShipmentFreightClass.FC_77_5,
    FC_85: FbaTransportV2024ShipmentFreightClass.FC_85,
    FC_92_5: FbaTransportV2024ShipmentFreightClass.FC_92_5,
    FC_100: FbaTransportV2024ShipmentFreightClass.FC_100,
    FC_110: FbaTransportV2024ShipmentFreightClass.FC_110,
    FC_125: FbaTransportV2024ShipmentFreightClass.FC_125,
    FC_150: FbaTransportV2024ShipmentFreightClass.FC_150,
    FC_175: FbaTransportV2024ShipmentFreightClass.FC_175,
    FC_200: FbaTransportV2024ShipmentFreightClass.FC_200,
    FC_250: FbaTransportV2024ShipmentFreightClass.FC_250,
    FC_300: FbaTransportV2024ShipmentFreightClass.FC_300,
    FC_400: FbaTransportV2024ShipmentFreightClass.FC_400,
    FC_500: FbaTransportV2024ShipmentFreightClass.FC_500,
};

const operationStatuses: Record<OperationRaw['status'], FbaTransportV2024OperationStatus> = {
    SUCCESS: FbaTransportV2024OperationStatus.Success,
    IN_PROGRESS: FbaTransportV2024OperationStatus.InProgress,
    FAILED: FbaTransportV2024OperationStatus.Failed,
};

export function toCurrency(raw: CurrencyRaw): FbaTransportV2024Currency {
    return {
        amount: raw.amount,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the spec leaves code a free string, but FbaTranportTotalFeeWithDiscount, FbaTransportFeesOverlay, FbaTransportPackingOptionIncentives and FbaTransportWindowOptions hand it to LocalMoney and money, which require dinero's Currency
        code: raw.code as Currency,
    };
}

export function toIncentive(raw: IncentiveRaw): FbaTransportV2024Incentive {
    return {
        description: raw.description,
        target: raw.target,
        type: raw.type,
        value: toCurrency(raw.value),
    };
}

export function toAddress(raw: AddressRaw): FbaTransportV2024Address {
    return {
        name: raw.name,
        addressLine1: raw.address_line_1,
        addressLine2: raw.address_line_2,
        companyName: raw.company_name,
        districtOrCounty: raw.district_or_county,
        city: raw.city,
        stateOrProvinceCode: raw.state_or_province_code,
        countryCode: raw.country_code,
        postalCode: raw.postal_code,
        email: raw.email,
        phoneNumber: raw.phone_number,
    };
}

export function toPlanItem(raw: PlanItemRaw): FbaTransportV2024PlanItem {
    return {
        id: raw.id,
        msku: raw.msku,
        fnsku: raw.fnsku,
        asin: raw.asin,
        quantity: raw.quantity,
        labelOwner: labelOwners[raw.label_owner],
        prepOwner: prepOwners[raw.prep_owner],
        expiration: raw.expiration,
        manufacturingLotCode: raw.manufacturing_lot_code,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
    };
}

function toShippingConfiguration(raw: ShippingConfigurationRaw): FbaTransportV2024ShippingConfiguration {
    return {
        shippingMode: raw.shipping_mode,
        shippingSolution: raw.shipping_solution,
    };
}

export function toPackingGroupItem(raw: PackingGroupItemRaw): FbaTransportV2024PackingGroupItem {
    return {
        id: raw.id,
        planItemId: raw.plan_item_id,
        quantity: raw.quantity,
    };
}

export function toPackingGroup(raw: PackingGroupRaw): FbaTransportV2024PackingGroup {
    return {
        id: raw.id,
        items: raw.items.map(toPackingGroupItem),
    };
}

export function toPackingOption(raw: PackingOptionRaw): FbaTransportV2024PackingOption {
    return {
        id: raw.id,
        discounts: raw.discounts.map(toIncentive),
        expiration: raw.expiration,
        fees: raw.fees.map(toIncentive),
        packingOptionId: raw.packing_option_id,
        status: raw.status,
        supportedShippingConfigurations: raw.supported_shipping_configurations.map(toShippingConfiguration),
        confirmedAt: raw.confirmed_at,
        packingGroups: raw.packing_groups.map(toPackingGroup),
    };
}

export function toBoxGroupItem(raw: BoxGroupItemRaw): FbaTransportBoxGroupItem {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        planItemId: raw.plan_item_id,
        msku: raw.msku,
        quantity: raw.quantity,
    };
}

export function toBoxGroup(raw: BoxGroupRaw): FbaTransportBoxGroup {
    return {
        id: raw.id,
        lengthMm: raw.length_mm,
        widthMm: raw.width_mm,
        heightMm: raw.height_mm,
        weightGm: raw.weight_gm,
        packingGroupId: raw.packing_group_id,
        boxNumbers: raw.box_numbers,
        items: raw.items.map(toBoxGroupItem),
    };
}

export function toShipmentBoxGroup(raw: ShipmentBoxGroupEntryRaw): FbaTransportV2024ShipmentBoxGroupEntry {
    if (Array.isArray(raw)) return null;

    return {
        id: raw.id,
        lengthMm: raw.length_mm,
        widthMm: raw.width_mm,
        heightMm: raw.height_mm,
        weightGm: raw.weight_gm,
        numberOfBoxes: raw.number_of_boxes,
        boxNumbers: raw.box_numbers,
        items: raw.items.map(toBoxGroupItem),
        packingGroupId: undefined,
    };
}

export function toShipmentDestination(raw: ShipmentDestinationRaw): FbaTransportV2024ShipmentDestination {
    return {
        address: raw.address === null ? null : toAddress(raw.address),
        destinationType: destinationTypes[raw.destination_type],
        warehouseId: raw.warehouse_id,
    };
}

export function toShipmentSource(raw: ShipmentSourceRaw): FbaTransportV2024ShipmentSource {
    return {
        address: raw.address === null ? null : toAddress(raw.address),
        sourceType: sourceTypes[raw.source_type],
    };
}

export function toContactInformation(raw: ContactInformationRaw): NonNullable<FbaTransportV2024Shipment['contactInformation']> {
    return {
        name: raw.name,
        email: raw.email,
        phoneNumber: raw.phone_number,
    };
}

export function toFreightInformation(raw: FreightInformationRaw): FbaTransportV2024ShipmentFreightInformation {
    return {
        freightClass: raw.freight_class === null ? null : freightClasses[raw.freight_class],
        declaredValue: raw.declaredValue === null ? null : toCurrency(raw.declaredValue),
    };
}

export function toTrackingDetails(raw: TrackingDetailsRaw): FbaTransportV2024Shipment['trackingDetails'] {
    if (raw === null) return null;

    return {
        ltlTrackingDetail: raw.ltl_tracking_detail === null ? null : {
            billOfLadingNumber: raw.ltl_tracking_detail.bill_of_lading_number,
            freightBillNumber: raw.ltl_tracking_detail.freight_bill_number,
        },
        spdTrackingDetail: raw.spd_tracking_detail === null ? null : {
            spdTrackingItems: raw.spd_tracking_detail.spd_tracking_items === null ? null : raw.spd_tracking_detail.spd_tracking_items.map(item => ({
                boxId: item.box_id,
                trackingId: item.tracking_id,
                trackingNumberValidationStatus: item.tracking_number_validation_status,
            })),
        },
    };
}

export function toShipmentBox(raw: ShipmentBoxRaw): FbaTransportV2024ShipmentBox {
    return {
        id: raw.id,
        boxGroupId: raw.box_group_id,
        amazonBoxId: raw.amazon_box_id,
        packageId: undefined,
        boxNumber: raw.box_number,
    };
}

export function toShipmentItem(raw: ShipmentItemRaw): FbaTransportV2024ShipmentItem {
    return {
        id: raw.id,
        planItemId: raw.plan_item_id,
        shipmentId: raw.shipment_id,
        quantity: raw.quantity,
    };
}

export function toPallet(raw: PalletRaw): FbaTransportV2024Pallet {
    return {
        id: raw.id,
        lengthMm: raw.length_mm,
        widthMm: raw.width_mm,
        heightMm: raw.height_mm,
        weightGm: raw.weight_gm,
        repeat: raw.repeat,
        stackable: raw.stackable,
    };
}

export function toDeliveryWindowOption(raw: DeliveryWindowOptionRaw): FbaTransportV2024DeliveryWindowOption {
    return {
        id: raw.id,
        createPlanOperationId: undefined,
        deliveryWindowOptionId: raw.delivery_window_option_id,
        shipmentId: raw.shipment_id,
        availabilityType: raw.availability_type,
        discounts: raw.discounts.map(toIncentive),
        startDate: raw.start_date,
        endDate: raw.end_date,
        validUntil: raw.valid_until,
        confirmedAt: raw.confirmed_at,
    };
}

export function toTransportationOption(raw: TransportationOptionRaw): FbaTransportV2024TransportationOption {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        createPlanOperationId: undefined,
        transportationOptionId: raw.transportation_option_id,
        shipmentId: raw.shipment_id,
        shippingMode: raw.shipping_mode,
        shippingSolution: raw.shipping_solution,
        carrier: {
            alphaCode: raw.carrier.alpha_code,
            name: raw.carrier.name,
        },
        carrierAppointment: raw.carrier_appointment === null ? null : {
            startTime: raw.carrier_appointment.start_time,
            endTime: raw.carrier_appointment.end_time,
        },
        preconditions: raw.preconditions,
        quote: raw.quote === null ? null : {
            cost: toCurrency(raw.quote.cost),
        },
        confirmedAt: raw.confirmed_at,
    };
}

export function toShipment(raw: ShipmentRaw): FbaTransportV2024Shipment {
    return {
        id: raw.id,
        shipmentId: raw.shipment_id,
        amazonReferenceId: raw.amazon_reference_id,
        destination: toShipmentDestination(raw.destination),
        source: toShipmentSource(raw.source),
        status: raw.status === null ? null : shipmentStatuses[raw.status],
        shipmentConfirmationId: raw.shipment_confirmation_id,
        firstAvailabilityDate: raw.first_availability_date,
        transportModePreference: raw.transport_mode_preference === null ? null : transportModePreferences[raw.transport_mode_preference],
        trackingDetails: toTrackingDetails(raw.tracking_details),
        contactInformation: toContactInformation(raw.contact_information),
        freightInformation: toFreightInformation(raw.freight_information),
        selectedTransportationOptionId: raw.selected_transportation_option_id,
        selectedDeliveryWindow: raw.selected_delivery_window,
        pallets: raw.pallets.map(toPallet),
        boxGroups: raw.box_groups.map(toShipmentBoxGroup),
        boxes: raw.boxes.map(toShipmentBox),
        items: raw.items.map(toShipmentItem),
        deliveryWindowOptions: raw.delivery_window_options.map(toDeliveryWindowOption),
        transportationOptions: raw.transportation_options.map(toTransportationOption),
    };
}

export function toPlacementOption(raw: PlacementOptionRaw): FbaTransportV2024PlacementOption {
    return {
        id: raw.id,
        discounts: raw.discounts.map(toIncentive),
        expiration: raw.expiration,
        fees: raw.fees.map(toIncentive),
        shipmentIds: raw.shipment_ids,
        placementOptionId: raw.placement_option_id,
        status: raw.status,
        isSelected: raw.is_selected,
        confirmedAt: raw.confirmed_at,
        shipments: raw.shipments.map(toShipment),
    };
}

export function toFbaTransportV2024Plan(raw: FbaTransportV2024PlanRaw): FbaTransportV2024Plan {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        name: raw.name,
        outboundShipmentId: raw.outbound_shipment_id,
        channelId: raw.channel_id,
        inboundPlanId: raw.inbound_plan_id,
        createPlanOperationId: raw.create_plan_operation_id,
        isCanceled: raw.is_canceled,
        using2dBarcodes: raw.using_2d_barcodes,
        status: planStatuses[raw.status],
        shipFromAddress: toAddress(raw.ship_from_address),
        marketplace: {
            identifier: raw.marketplace.identifier,
            country: raw.marketplace.country,
        },
        items: raw.items.map(toPlanItem),
        packingOptions: raw.packing_options.map(toPackingOption),
        boxGroups: raw.box_groups.map(toBoxGroup),
        placementOptions: raw.placement_options.map(toPlacementOption),
        isPackingInformationSet: raw.is_packing_information_set,
        isPackingInfoKnown: raw.isPackingInfoKnown,
        isMarkedAsDeleted: undefined,
    };
}

export function toLabel(raw: LabelRaw): FbaTransportV2024Label {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        name: raw.name,
        url: raw.url,
        type: raw.type,
        paperSize: raw.paper_size,
    };
}

export function toOperationProblem(raw: OperationProblemRaw): FbaTransportV2024OperationProblem {
    return {
        code: raw.code,
        message: raw.message,
        details: raw.details,
        severity: raw.severity,
    };
}

export function toOperation(raw: OperationRaw): FbaTransportV2024Operation {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        channelId: raw.channel_id,
        operationId: raw.operation_id,
        status: operationStatuses[raw.status],
        description: raw.description,
        problems: raw.problems === null ? null : raw.problems.map(toOperationProblem),
    };
}

export function toImportedFbaPlan(raw: ImportedFbaPlanRaw): ImportedFbaPlan {
    return {
        id: undefined,
        createdAt: raw.created_at,
        lastUpdatedAt: raw.last_updated_at,
        inboundPlanId: raw.inbound_plan_id,
        marketplaceIds: raw.marketplace_ids,
        name: raw.name,
        sourceAddress: toAddress(raw.source_address),
        status: planStatuses[raw.status],
    };
}
